/**
 * EL-186 — o menu ⋮ dos cartões de lista.
 *
 * Reusa as classes que já existem no estilo.css (`.mkebab` para o botão, `.treemenu`
 * para o painel), então nasce igual aos menus do simulador sem CSS novo.
 *
 * ⚠ O painel é CRIADO E DESTRUÍDO a cada abertura, de propósito. Um `.treemenu` fixo
 * pendurado no `<body>` seria apagado na primeira saída de um cenário: o
 * `desmontarSimulador` do legado varre `body > .treemenu` para limpar o que ele
 * mesmo pendura, e não tem como saber que aquele era de outro dono.
 *
 * ⚠ A largura 172px está duplicada no estilo.css e aqui, no cálculo de posição —
 * é o combinado registrado lá, para o menu não nascer fora da tela.
 */
import { h } from '../dom';

const LARGURA = 172;

export interface ItemMenu {
  rotulo: string;
  /** Marca em vermelho: usar só para o que destrói ou pede para destruir. */
  perigo?: boolean;
  /**
   * O MOTIVO de o item não estar disponível agora (09/10/2026 — "Copiar" com as 5 vagas
   * ocupadas). O item aparece desligado, com o motivo na dica: sumir com ele esconderia que
   * a ação existe, e deixá-lo ativo responderia "não pode" só depois do clique.
   */
  desabilitado?: string | null;
  aoClicar: () => void;
}

let aberto: { painel: HTMLElement; fechar: () => void } | null = null;

/** Fecha o menu que estiver aberto, se houver. Idempotente. */
export function fecharMenu(): void {
  aberto?.fechar();
}

/**
 * Há um menu ⋮ aberto AGORA? Quem também ouve o Esc (o painel do seletor de cenários) pergunta
 * aqui, e não ao DOM: procurar `body > .treemenu` casava com o `#tree-menu` da v67, que fica
 * sempre no <body> (escondido) enquanto o simulador está montado — e o Esc nunca fechava nada.
 */
export function menuAberto(): boolean {
  return aberto !== null;
}

/** Abre o menu ancorado no elemento (tipicamente o próprio botão ⋮). */
export function abrirMenu(ancora: HTMLElement, itens: ItemMenu[]): void {
  /* Clicar no mesmo botão de novo fecha — é o que a mão espera. */
  const jaEra = aberto;
  fecharMenu();
  if (jaEra !== null && jaEra.painel.dataset.ancora === ancoraId(ancora)) return;

  const painel = h('div', { class: 'treemenu open', 'data-el': 'EL-186' });
  painel.dataset.ancora = ancoraId(ancora);

  const fechar = (): void => {
    document.removeEventListener('mousedown', aoClicarFora, true);
    document.removeEventListener('keydown', aoTeclar, true);
    window.removeEventListener('resize', acompanhar);
    window.removeEventListener('scroll', acompanhar, true);
    painel.remove();
    if (aberto?.painel === painel) aberto = null;
  };
  const aoClicarFora = (e: Event): void => {
    if (!(e.target as HTMLElement | null)?.closest('.treemenu')) fechar();
  };
  const aoTeclar = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') fechar();
  };

  for (const item of itens) {
    painel.appendChild(
      h(
        'button',
        {
          class: item.perigo === true ? 'danger' : '',
          disabled: typeof item.desabilitado === 'string',
          onclick: (e: Event) => {
            e.stopPropagation();
            fechar();
            item.aoClicar();
          },
        },
        item.rotulo,
        /* O motivo vai ESCRITO no item, não só na dica: botão desligado não recebe o mouse
           em todo navegador, e a dica dele pode nunca aparecer. */
        typeof item.desabilitado === 'string' ? h('span', { class: 'tm-motivo' }, item.desabilitado) : null,
      ),
    );
  }

  document.body.appendChild(painel);

  /* Posicionar só DEPOIS de estar no documento: antes disso a altura é zero e o
     menu de baixo da lista abriria para fora da janela. */
  function posicionar(): void {
    const r = ancora.getBoundingClientRect();
    const alt = painel.getBoundingClientRect().height;
    const esq = Math.max(8, Math.min(r.right - LARGURA, window.innerWidth - LARGURA - 8));
    const topo = r.bottom + 4 + alt > window.innerHeight ? Math.max(8, r.top - alt - 4) : r.bottom + 4;
    painel.style.left = `${esq}px`;
    painel.style.top = `${topo}px`;
  }
  posicionar();

  /**
   * Rolou: o menu é `position:fixed` e ficaria parado enquanto a lista anda por baixo.
   * ACOMPANHA a âncora em vez de fechar — fechar parecia razoável e era um defeito:
   * o próprio navegador rola a página para trazer o item clicado à vista, e com a lista
   * longa isso acontecia ANTES do clique. O menu sumia sozinho no caminho do dedo.
   * Só fecha quando a âncora sai da janela: menu preso a algo invisível é lixo na tela.
   */
  function acompanhar(): void {
    const r = ancora.getBoundingClientRect();
    if (r.bottom < 0 || r.top > window.innerHeight) { fechar(); return; }
    posicionar();
  }

  document.addEventListener('mousedown', aoClicarFora, true);
  document.addEventListener('keydown', aoTeclar, true);
  window.addEventListener('resize', acompanhar);
  window.addEventListener('scroll', acompanhar, true);
  aberto = { painel, fechar };
}

/** Botão ⋮ pronto, com o `stopPropagation` que impede o cartão de abrir junto. */
export function botaoMenu(itens: () => ItemMenu[], titulo = 'Mais ações'): HTMLElement {
  const b = h(
    'button',
    {
      class: 'mkebab',
      title: titulo,
      onclick: (e: Event) => {
        e.stopPropagation();
        abrirMenu(b, itens());
      },
    },
    '⋮',
  );
  return b;
}

let seq = 0;
const ids = new WeakMap<HTMLElement, string>();
function ancoraId(el: HTMLElement): string {
  let id = ids.get(el);
  if (id === undefined) {
    seq += 1;
    id = `a${seq}`;
    ids.set(el, id);
  }
  return id;
}
